import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, updateDoc } from "firebase/firestore";
import { db } from "../firebase/firebase-config";

const formatAmount = (value) => `Rs. ${Number(value || 0).toFixed(2)}`;

const MobileBookingsSubmitted = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [actionId, setActionId] = useState("");

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const snapshot = await getDocs(collection(db, "mobile-bookings"));
        const data = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        setBookings(data);
      } catch (error) {
        console.error("Unable to fetch mobile bookings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const submittedBookings = useMemo(() => {
    const term = search.trim().toLowerCase();
    return bookings
      .filter((item) => item.status === "submitted")
      .filter((item) => {
        if (!term) return true;
        return [
          item.bookerName,
          item.bookingSite,
          item.itemDescription,
          item.cardHolder,
          item.cardNumber,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      })
      .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
  }, [bookings, search]);

  const updateStatus = async (bookingId, status) => {
    setActionId(bookingId);
    setMessage("");
    try {
      const nextValues = {
        status,
        statusUpdatedAt: new Date().toISOString(),
      };

      if (status === "accepted") {
        nextValues.deliveryStatus = "not_delivered";
      }

      await updateDoc(doc(db, "mobile-bookings", bookingId), nextValues);

      setBookings((prev) =>
        prev.map((item) =>
          item.id === bookingId
            ? {
                ...item,
                ...nextValues,
              }
            : item
        )
      );
      setMessage(`Booking ${status === "accepted" ? "accepted" : "rejected"} successfully.`);
    } catch (error) {
      console.error("Unable to update booking status:", error);
      setMessage("Unable to update booking right now.");
    } finally {
      setActionId("");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">Mobile Booking Submitted</h2>
          <p className="text-muted mb-0">Review incoming bookings and accept or reject them.</p>
        </div>
        <input
          type="text"
          className="form-control"
          placeholder="Search by booker, retailer, item or card holder"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ maxWidth: 360, borderRadius: 14 }}
        />
      </div>

      {message && (
        <div className={`alert ${message.includes("Unable") ? "alert-danger" : "alert-success"} rounded-4`}>
          {message}
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-bordered table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Booker</th>
              <th>Booking Site</th>
              <th>Item</th>
              <th>Card Holder</th>
              <th>Card Number</th>
              <th>Transaction</th>
              <th>Remarks</th>
              <th>Submitted</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="text-center py-4">
                  Loading bookings...
                </td>
              </tr>
            ) : submittedBookings.length ? (
              submittedBookings.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>{item.bookerName}</td>
                  <td>{item.bookingSite}</td>
                  <td>{item.itemDescription}</td>
                  <td>{item.cardHolder}</td>
                  <td>{item.cardNumber}</td>
                  <td>{formatAmount(item.transactionAmount)}</td>
                  <td>{item.remarks || "-"}</td>
                  <td>{item.createdAt ? new Date(item.createdAt).toLocaleString("en-IN") : "-"}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        disabled={actionId === item.id}
                        onClick={() => updateStatus(item.id, "accepted")}
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled={actionId === item.id}
                        onClick={() => updateStatus(item.id, "rejected")}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="text-center py-4">
                  No submitted bookings found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MobileBookingsSubmitted;
